import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  CardActions,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as DuplicateIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Print as PrintIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { LabelTemplate } from './LabelDesigner';

interface TemplateLibraryProps {
  templates: LabelTemplate[];
  onSelectTemplate: (templateId: string) => void;
  onCreateTemplate: () => void;
  onDuplicateTemplate: (templateId: string) => void;
  onDeleteTemplate: (templateId: string) => void;
  onPrintTemplate: (templateId: string) => void;
}

const TemplateLibrary: React.FC<TemplateLibraryProps> = ({
  templates,
  onSelectTemplate,
  onCreateTemplate,
  onDuplicateTemplate,
  onDeleteTemplate,
  onPrintTemplate
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);
  
  // Filter templates based on search term and filter type
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || template.type === filterType;
    return matchesSearch && matchesType;
  });
  
  // Group templates by type for better organization
  const groupedTemplates: Record<string, LabelTemplate[]> = {};
  
  // Helper to get type display name
  const getTypeDisplayName = (type: string) => {
    switch (type) {
      case 'shelf': return 'Shelf Labels';
      case 'item': return 'Item Labels';
      case 'barcode': return 'Barcode Labels';
      case 'custom': return 'Custom Labels';
      default: return 'Other Labels';
    }
  };
  
  // Group templates
  filteredTemplates.forEach(template => {
    if (!groupedTemplates[template.type]) {
      groupedTemplates[template.type] = [];
    }
    groupedTemplates[template.type].push(template);
  });
  
  // Handle opening the delete confirmation dialog
  const handleConfirmDelete = (templateId: string) => {
    setTemplateToDelete(templateId);
    setDeleteDialogOpen(true);
  };
  
  // Handle deleting a template
  const handleDelete = () => {
    if (templateToDelete) {
      onDeleteTemplate(templateToDelete);
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    }
  };
  
  // Render a template card
  const renderTemplateCard = (template: LabelTemplate) => {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardActionArea 
          onClick={() => onSelectTemplate(template.id)}
          sx={{ flexGrow: 1 }}
        >
          <CardContent>
            <Typography variant="h6" noWrap>
              {template.name}
            </Typography>
            
            <Box sx={{ mt: 1, mb: 2 }}>
              <Chip
                label={getTypeDisplayName(template.type)}
                size="small"
                color={
                  template.type === 'shelf' ? 'primary' :
                  template.type === 'item' ? 'secondary' :
                  template.type === 'barcode' ? 'info' : 
                  'default'
                }
                sx={{ mr: 0.5 }}
              />
              
              {template.isDefault && (
                <Chip
                  label="Default"
                  size="small"
                  color="success"
                />
              )}
            </Box>
            
            <Typography variant="body2" color="text.secondary">
              Size: {template.width}" × {template.height}"
            </Typography>
            
            <Typography variant="body2" color="text.secondary">
              Elements: {template.elements.length}
            </Typography>
          </CardContent>
        </CardActionArea>
        
        <CardActions>
          <IconButton
            size="small"
            onClick={() => onSelectTemplate(template.id)}
            color="primary"
          >
            <EditIcon />
          </IconButton>
          
          <IconButton
            size="small"
            onClick={() => onDuplicateTemplate(template.id)}
          >
            <DuplicateIcon />
          </IconButton>
          
          <IconButton
            size="small"
            onClick={() => onPrintTemplate(template.id)}
            color="success"
          >
            <PrintIcon />
          </IconButton>
          
          <Box sx={{ flexGrow: 1 }} />
          
          <IconButton
            size="small"
            onClick={() => handleConfirmDelete(template.id)}
            color="error"
            disabled={template.isDefault}
          >
            <DeleteIcon />
          </IconButton>
        </CardActions>
      </Card>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Label Templates</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onCreateTemplate}
        >
          Create Template
        </Button>
      </Box>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by Type</InputLabel>
              <Select
                value={filterType}
                label="Filter by Type"
                onChange={(e) => setFilterType(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <FilterIcon />
                  </InputAdornment>
                }
              >
                <MenuItem value="all">All Templates</MenuItem>
                <MenuItem value="shelf">Shelf Labels</MenuItem>
                <MenuItem value="item">Item Labels</MenuItem>
                <MenuItem value="barcode">Barcode Labels</MenuItem>
                <MenuItem value="custom">Custom Labels</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
      {Object.keys(groupedTemplates).length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            No templates found. Create your first template to get started.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onCreateTemplate}
          >
            Create Template
          </Button>
        </Paper>
      ) : (
        Object.entries(groupedTemplates).map(([type, typeTemplates]) => (
          <Box key={type} sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              {getTypeDisplayName(type)}
            </Typography>
            
            <Grid container spacing={2}>
              {typeTemplates.map(template => (
                <Grid key={template.id} item xs={12} sm={6} md={4} lg={3}>
                  {renderTemplateCard(template)}
                </Grid>
              ))}
            </Grid>
          </Box>
        ))
      )}
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Template?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this template? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateLibrary; 